import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { collection, query, onSnapshot, where, getDocs, orderBy, limit } from 'firebase/firestore';
import { db } from '../services/firebase';
import ContactUs from "./ContactUs";
import CalendarWidget from "./Schedule/CalendarWidget";
import FloatingAIChat from "./AIAssistant/FloatingAIChat";
import AnnouncementDashboard from "./Announcements/AnnouncementDashboard";

interface TenantDomain {
  id: string;
  schoolName: string;
  domain: string;
}

interface DashboardProps {
  onLogout: () => void;
}

interface Stats {
  totalStudents: number;
  teachingStaff: number;
  classesToday: number;
  pendingFees: number;
}

interface RecentActivity {
  id: string;
  type: 'announcement' | 'payment' | 'attendance' | 'admission' | 'other';
  title: string;
  description: string;
  timestamp: Date;
}

const Dashboard: React.FC<DashboardProps> = ({ onLogout }) => {
  const navigate = useNavigate();
  const [activeModule, setActiveModule] = useState<string | null>(null);
  const [domain, setDomain] = useState<string>('');
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [schoolName, setSchoolName] = useState<string>('Smart School');
  const [userName, setUserName] = useState<string>('Admin');
  const [stats, setStats] = useState<Stats>({
    totalStudents: 0,
    teachingStaff: 0,
    classesToday: 0,
    pendingFees: 0,
  });
  const [loading, setLoading] = useState<boolean>(true);
  const [recentActivities, setRecentActivities] = useState<RecentActivity[]>([]);

  // Super user state
  const [isSuperUser, setIsSuperUser] = useState<boolean>(false);
  const [allowedDomains, setAllowedDomains] = useState<string[]>([]);
  const [tenantDomains, setTenantDomains] = useState<TenantDomain[]>([]);
  const [showDomainDropdown, setShowDomainDropdown] = useState(false);
  const [domainSearch, setDomainSearch] = useState('');
  const domainDropdownRef = useRef<HTMLDivElement>(null);

  // Get domain and user info from localStorage (set during login)
  useEffect(() => {
    const userData = localStorage.getItem('userData');
    if (userData) {
      const user = JSON.parse(userData);
      const superUser = user.isSuperUser || user.superAdmin === true;
      setIsSuperUser(superUser);
      setUserName(user.name || 'Admin');

      if (superUser) {
        // Super user: store their allowed domains list
        const domains: string[] = user.domains || [];
        setAllowedDomains(domains);

        // Use activeDomain from localStorage, or fall back to first allowed domain / user's own domain
        const activeDomain = localStorage.getItem('activeDomain') || (domains.length > 0 ? domains[0] : user.domain);
        setDomain(activeDomain);
        // schoolName will be updated when tenantDomains are fetched
        setSchoolName(activeDomain);
      } else {
        setDomain(user.domain || 'Smart School');
        setSchoolName(user.schoolName || user.domain || 'Smart School');
      }
    }
  }, []);

  // Fetch tenant domains for super users (only allowed domains)
  useEffect(() => {
    if (!isSuperUser || allowedDomains.length === 0) return;

    const tenantsRef = collection(db, 'tenant-domains');
    const unsubscribe = onSnapshot(tenantsRef, (snapshot) => {
      const allDomains: TenantDomain[] = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        allDomains.push({
          id: doc.id,
          schoolName: data.schoolName || doc.id,
          domain: doc.id,
        });
      });

      // Filter to only the domains this super user is allowed to access
      const filtered = allDomains.filter(t => allowedDomains.includes(t.domain));
      filtered.sort((a, b) => a.schoolName.localeCompare(b.schoolName));
      setTenantDomains(filtered);

      // Update schoolName from tenant data for current domain
      const currentTenant = filtered.find(t => t.domain === domain);
      if (currentTenant) {
        setSchoolName(currentTenant.schoolName);
      }
    });

    return () => unsubscribe();
  }, [isSuperUser, allowedDomains, domain]);

  // Close domain dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (domainDropdownRef.current && !domainDropdownRef.current.contains(event.target as Node)) {
        setShowDomainDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Switch domain for super user
  const handleDomainSwitch = (newDomain: string, newSchoolName: string) => {
    setDomain(newDomain);
    setSchoolName(newSchoolName);
    localStorage.setItem('activeDomain', newDomain);
    setShowDomainDropdown(false);
    setDomainSearch('');

    // Reset stats so they reload for the new domain
    setStats({ totalStudents: 0, teachingStaff: 0, classesToday: 0, pendingFees: 0 });
    setLoading(true);
    setRecentActivities([]);
  };

  // Fetch real-time statistics from Firestore
  useEffect(() => {
    if (!domain) return;

    const unsubscribers: (() => void)[] = [];

    // Listen to students collection for real-time updates
    const studentsRef = collection(db, `school-users/${domain}/students`);
    const unsubStudents = onSnapshot(studentsRef, (snapshot) => {
      setStats(prev => ({ ...prev, totalStudents: snapshot.size }));
    });
    unsubscribers.push(unsubStudents);

    // Fetch teaching staff count (assuming teachers are in a 'teachers' or 'staff' collection)
    // For now, we'll use a placeholder or you can create a teachers collection
    const fetchTeachersCount = async () => {
      try {
        const teachersRef = collection(db, `school-users/${domain}/teachers`);
        const snapshot = await getDocs(teachersRef);
        setStats(prev => ({ ...prev, teachingStaff: snapshot.size }));
      } catch (error) {
        console.log('Teachers collection not found, using default value');
        setStats(prev => ({ ...prev, teachingStaff: 0 }));
      }
    };
    fetchTeachersCount();

    // Fetch timetable count for today's classes
    const fetchTodayClasses = async () => {
      try {
        const timetableRef = collection(db, `school-users/${domain}/timetables`);
        const snapshot = await getDocs(timetableRef);

        // Get unique classes (since each class has multiple days)
        const uniqueClasses = new Set<string>();
        snapshot.forEach(doc => {
          const data = doc.data();
          if (data.classId) {
            uniqueClasses.add(data.classId);
          }
        });

        setStats(prev => ({ ...prev, classesToday: uniqueClasses.size }));
      } catch (error) {
        console.log('Timetable collection not found');
        setStats(prev => ({ ...prev, classesToday: 0 }));
      }
    };
    fetchTodayClasses();

    // Fetch pending fees (assuming fees collection exists)
    const fetchPendingFees = async () => {
      try {
        const feesRef = collection(db, `school-users/${domain}/fees`);
        const q = query(feesRef, where('status', '==', 'pending'));
        const snapshot = await getDocs(q);

        let totalPending = 0;
        snapshot.forEach(doc => {
          const data = doc.data();
          if (data.amount) {
            totalPending += Number(data.amount);
          }
        });

        setStats(prev => ({ ...prev, pendingFees: totalPending }));
      } catch (error) {
        console.log('Fees collection not found');
        setStats(prev => ({ ...prev, pendingFees: 0 }));
      }
    };
    fetchPendingFees();

    setLoading(false);

    // Cleanup listeners on unmount
    return () => {
      unsubscribers.forEach(unsub => unsub());
    };
  }, [domain]);

  // Fetch recent activities from various collections
  useEffect(() => {
    if (!domain) return;

    const fetchRecentActivities = async () => {
      const activities: RecentActivity[] = [];

      try {
        // Fetch recent announcements
        const announcementsRef = collection(db, `school-users/${domain}/announcements`);
        const announcementsQuery = query(announcementsRef, orderBy('createdAt', 'desc'), limit(5));
        const announcementsSnapshot = await getDocs(announcementsQuery);
        announcementsSnapshot.forEach((doc) => {
          const data = doc.data();
          activities.push({
            id: doc.id,
            type: 'announcement',
            title: 'New Announcement',
            description: data.title || 'Announcement sent',
            timestamp: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(data.createdAt),
          });
        });
      } catch (error) {
        console.log('Error fetching announcements:', error);
      }

      try {
        // Fetch recent fee payments
        const feeAssignmentsRef = collection(db, `school-users/${domain}/fee-assignments`);
        const feeQuery = query(feeAssignmentsRef, orderBy('lastPaymentDate', 'desc'), limit(5));
        const feeSnapshot = await getDocs(feeQuery);
        feeSnapshot.forEach((doc) => {
          const data = doc.data();
          if (data.lastPaymentDate) {
            activities.push({
              id: doc.id,
              type: 'payment',
              title: 'Fee Payment',
              description: `${data.studentName || 'Student'} - ₹${data.lastPaymentAmount || 0}`,
              timestamp: new Date(data.lastPaymentDate),
            });
          }
        });
      } catch (error) {
        console.log('Error fetching fee payments:', error);
      }

      try {
        // Fetch recent attendance records
        const attendanceRef = collection(db, `school-users/${domain}/attendance`);
        const attendanceQuery = query(attendanceRef, orderBy('date', 'desc'), limit(5));
        const attendanceSnapshot = await getDocs(attendanceQuery);
        attendanceSnapshot.forEach((doc) => {
          const data = doc.data();
          activities.push({
            id: doc.id,
            type: 'attendance',
            title: 'Attendance Marked',
            description: `Class ${data.classId || data.className || 'N/A'}`,
            timestamp: data.date?.toDate ? data.date.toDate() : new Date(data.date),
          });
        });
      } catch (error) {
        console.log('Error fetching attendance:', error);
      }

      try {
        // Fetch recent student admissions
        const studentsRef = collection(db, `school-users/${domain}/students`);
        const studentsQuery = query(studentsRef, orderBy('createdAt', 'desc'), limit(5));
        const studentsSnapshot = await getDocs(studentsQuery);
        studentsSnapshot.forEach((doc) => {
          const data = doc.data();
          if (data.createdAt) {
            activities.push({
              id: doc.id,
              type: 'admission',
              title: 'New Admission',
              description: `${data.name || 'Student'} - Grade ${data.class || 'N/A'}`,
              timestamp: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(data.createdAt),
            });
          }
        });
      } catch (error) {
        console.log('Error fetching students:', error);
      }

      // Sort all activities by timestamp (most recent first) and take top 5
      activities.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
      setRecentActivities(activities.slice(0, 5));
    };

    fetchRecentActivities();
  }, [domain]);

  if (activeModule === 'Contact') {
    return <ContactUs onBack={() => setActiveModule(null)} />;
  }

  if (activeModule === 'Announcements') {
    return <AnnouncementDashboard domain={domain} onBack={() => setActiveModule(null)} />;
  }

  const modules = [
    { id: 'SIS', name: 'Student Information', icon: 'bi-person-badge', color: '#5C2597', desc: 'Manage student data, attendance & profiles' },
    { id: 'HR', name: 'Human Resources', icon: 'bi-people', color: '#2D7DD2', desc: 'Employee management & payroll' },
    { id: 'Academics', name: 'Academics', icon: 'bi-book', color: '#28A745', desc: 'Assignments, exams, results & study materials' },
    { id: 'Finance', name: 'Finance', icon: 'bi-currency-dollar', color: '#FFC107', desc: 'Fee collection & accounting' },
    { id: 'Analytics', name: 'Analytics', icon: 'bi-graph-up-arrow', color: '#6610F2', desc: 'Reports, workload analysis & performance' },
    { id: 'Schedule', name: 'Schedule', icon: 'bi-calendar3', color: '#20C997', desc: 'Manage holidays, events & academic calendar' },
    { id: 'Announcements', name: 'Announcements', icon: 'bi-megaphone-fill', color: '#DC3545', desc: 'Send & manage school announcements' },
    { id: 'Admissions', name: 'Admissions', icon: 'bi-clipboard-check', color: '#17A2B8', desc: 'Student enrollment & applications' },
    { id: 'Visitor', name: 'Visitor Management', icon: 'bi-person-check', color: '#6C757D', desc: 'Track visitors & appointments' },
    { id: 'Transport', name: 'Transport', icon: 'bi-bus-front', color: '#E83E8C', desc: 'Bus routes & vehicle tracking' },
    { id: 'Certificates', name: 'Certificates', icon: 'bi-award', color: '#FD7E14', desc: 'Generate & manage certificates' },
    { id: 'Library', name: 'Library', icon: 'bi-journal-bookmark', color: '#6F42C1', desc: 'Book inventory & circulation' },
    { id: 'Tickets', name: 'Complaint Box', icon: 'bi-ticket-detailed', color: '#FF6B6B', desc: 'View & manage complaint tickets' },
    { id: 'PhotoGallery', name: 'Photo Gallery', icon: 'bi-images', color: '#0DCAF0', desc: 'Upload & browse school photo gallery' },
  ];

  // Format numbers with commas
  const formatNumber = (num: number): string => {
    return num.toLocaleString('en-IN');
  };

  // Format currency in Indian format
  const formatCurrency = (amount: number): string => {
    if (amount >= 10000000) {
      return `₹ ${(amount / 10000000).toFixed(1)}Cr`;
    } else if (amount >= 100000) {
      return `₹ ${(amount / 100000).toFixed(1)}L`;
    } else if (amount >= 1000) {
      return `₹ ${(amount / 1000).toFixed(1)}K`;
    }
    return `₹ ${amount}`;
  };

  // Format relative time (e.g., "2h ago", "3d ago")
  const formatTimeAgo = (date: Date): string => {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
  };

  const statsDisplay = [
    { label: 'Total Students', value: loading ? '...' : formatNumber(stats.totalStudents), icon: 'bi-people-fill', color: '#5C2597' },
    { label: 'Teaching Staff', value: loading ? '...' : formatNumber(stats.teachingStaff), icon: 'bi-person-workspace', color: '#2D7DD2' },
    { label: 'Classes Today', value: loading ? '...' : formatNumber(stats.classesToday), icon: 'bi-calendar-check', color: '#28A745' },
    { label: 'Pending Fees', value: loading ? '...' : formatCurrency(stats.pendingFees), icon: 'bi-cash-stack', color: '#FFC107' },
  ];

  const quickActions = [
    {
      label: 'Mark Attendance',
      icon: 'bi-calendar2-check',
      color: '#28A745',
      action: () => navigate('/dashboard/sis/attendance')
    },
    {
      label: 'View Analytics',
      icon: 'bi-graph-up-arrow',
      color: '#6610F2',
      action: () => navigate('/dashboard/analytics')
    },
    {
      label: 'Send Announcement',
      icon: 'bi-megaphone',
      color: '#DC3545',
      action: () => setActiveModule('Announcements')
    },
    {
      label: 'Modify Time Table',
      icon: 'bi-pencil-square',
      color: '#2D7DD2',
      action: () => navigate('/dashboard/schedule/timetable-modify')
    },
  ];

  return (
    <div className="bg-light min-vh-100">
      {/* Top Navbar */}
      <nav className="navbar navbar-expand-lg bg-primary-custom shadow">
        <div className="container-fluid px-4">
          <span className="navbar-brand text-white fw-bold fs-4 mb-0">
            <i className="bi bi-mortarboard-fill me-2"></i>
            {schoolName}
          </span>
          <div className="d-flex align-items-center gap-3">
            {/* Domain Switcher for Super Users */}
            {isSuperUser && (
              <div className="position-relative" ref={domainDropdownRef}>
                <button
                  className="btn btn-outline-light btn-sm d-flex align-items-center gap-2"
                  onClick={() => setShowDomainDropdown(!showDomainDropdown)}
                  style={{ borderColor: 'rgba(255,255,255,0.5)' }}
                >
                  <i className="bi bi-building"></i>
                  <span className="d-none d-md-inline" style={{ maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {domain}
                  </span>
                  <i className={`bi bi-chevron-${showDomainDropdown ? 'up' : 'down'}`}></i>
                </button>

                {showDomainDropdown && (
                  <div
                    className="position-absolute bg-white rounded shadow-lg border"
                    style={{
                      top: '100%',
                      right: 0,
                      width: '320px',
                      maxHeight: '400px',
                      zIndex: 1050,
                      marginTop: '4px',
                    }}
                  >
                    {/* Header */}
                    <div className="p-3 border-bottom" style={{ backgroundColor: '#f8f9fa' }}>
                      <div className="d-flex align-items-center mb-2">
                        <i className="bi bi-shield-check text-primary-custom me-2"></i>
                        <strong className="text-dark small">Switch Domain</strong>
                        <span className="badge bg-warning text-dark ms-auto" style={{ fontSize: '0.65rem' }}>Super Admin</span>
                      </div>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        placeholder="Search domains..."
                        value={domainSearch}
                        onChange={(e) => setDomainSearch(e.target.value)}
                        autoFocus
                      />
                    </div>

                    {/* Domain List */}
                    <div style={{ maxHeight: '280px', overflowY: 'auto' }}>
                      {tenantDomains
                        .filter(t =>
                          t.schoolName.toLowerCase().includes(domainSearch.toLowerCase()) ||
                          t.domain.toLowerCase().includes(domainSearch.toLowerCase())
                        )
                        .map((tenant) => (
                          <button
                            key={tenant.id}
                            className={`btn btn-link text-start w-100 text-decoration-none px-3 py-2 border-bottom ${
                              tenant.domain === domain ? 'bg-light' : ''
                            }`}
                            onClick={() => handleDomainSwitch(tenant.domain, tenant.schoolName)}
                            style={{ transition: 'background-color 0.15s' }}
                            onMouseEnter={(e) => { if (tenant.domain !== domain) e.currentTarget.style.backgroundColor = '#f0f0f0'; }}
                            onMouseLeave={(e) => { if (tenant.domain !== domain) e.currentTarget.style.backgroundColor = ''; }}
                          >
                            <div className="d-flex align-items-center">
                              <div
                                className="rounded-circle d-flex align-items-center justify-content-center me-2 flex-shrink-0"
                                style={{
                                  width: '32px',
                                  height: '32px',
                                  backgroundColor: tenant.domain === domain ? '#5C259720' : '#e9ecef',
                                  color: tenant.domain === domain ? '#5C2597' : '#6c757d',
                                  fontSize: '0.75rem',
                                  fontWeight: 'bold',
                                }}
                              >
                                {tenant.schoolName.charAt(0).toUpperCase()}
                              </div>
                              <div className="flex-grow-1 overflow-hidden">
                                <div className="fw-semibold text-dark small text-truncate">{tenant.schoolName}</div>
                                <div className="text-muted" style={{ fontSize: '0.7rem' }}>{tenant.domain}</div>
                              </div>
                              {tenant.domain === domain && (
                                <i className="bi bi-check-circle-fill text-success ms-2"></i>
                              )}
                            </div>
                          </button>
                        ))}
                      {tenantDomains.filter(t =>
                        t.schoolName.toLowerCase().includes(domainSearch.toLowerCase()) ||
                        t.domain.toLowerCase().includes(domainSearch.toLowerCase())
                      ).length === 0 && (
                        <div className="text-center text-muted py-3">
                          <i className="bi bi-search me-1"></i>
                          <small>No domains found</small>
                        </div>
                      )}
                    </div>

                    {/* Footer */}
                    <div className="p-2 border-top text-center" style={{ backgroundColor: '#f8f9fa' }}>
                      <small className="text-muted">
                        {tenantDomains.length} domain{tenantDomains.length !== 1 ? 's' : ''} available
                      </small>
                    </div>
                  </div>
                )}
              </div>
            )}

            <span className="text-white d-none d-md-inline">
              <i className="bi bi-person-circle me-2"></i>
              Welcome, {userName}
            </span>
            <button className="btn btn-light btn-sm" onClick={onLogout}>
              <i className="bi bi-box-arrow-right me-1"></i>
              Logout
            </button>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="container-fluid px-4 py-4">
        {/* Stats Cards */}
        <div className="row g-3 mb-4">
          {statsDisplay.map((stat, idx) => (
            <div key={idx} className="col-12 col-sm-6 col-lg-3">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body d-flex align-items-center">
                  <div
                    className="rounded-circle d-flex align-items-center justify-content-center me-3"
                    style={{
                      width: '60px',
                      height: '60px',
                      backgroundColor: `${stat.color}20`,
                      color: stat.color,
                    }}
                  >
                    <i className={`${stat.icon} fs-3`}></i>
                  </div>
                  <div>
                    <div className="text-muted small mb-1">{stat.label}</div>
                    <div className="fs-4 fw-bold">{stat.value}</div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="row g-4">
          {/* Modules Grid */}
          <div className="col-lg-8">
            <div className="card border-0 shadow-sm">
              <div className="card-body">
                <h5 className="card-title mb-4">
                  <i className="bi bi-grid-3x3-gap-fill me-2 text-primary-custom"></i>
                  Modules
                </h5>
                <div className="row g-3">
                  {modules.map((module) => (
                    <div key={module.id} className="col-12 col-md-6">
                      <div
                        className="card h-100 border-0 shadow-sm module-card"
                        style={{ cursor: 'pointer', transition: 'transform 0.2s, box-shadow 0.2s' }}
                        onClick={() => {
                          if (module.id === 'SIS') {
                            navigate('/dashboard/sis');
                          } else if (module.id === 'HR') {
                            navigate('/dashboard/hr');
                          } else if (module.id === 'Finance') {
                            navigate('/dashboard/finance');
                          } else if (module.id === 'Academics') {
                            navigate('/dashboard/academics');
                          } else if (module.id === 'Analytics') {
                            navigate('/dashboard/analytics');
                          } else if (module.id === 'Schedule') {
                            navigate('/dashboard/schedule');
                          } else if (module.id === 'Announcements') {
                            setActiveModule('Announcements');
                          } else if (module.id === 'Transport') {
                            navigate('/dashboard/transport');
                          } else if (module.id === 'Admissions') {
                            navigate('/dashboard/admissions');
                          } else if (module.id === 'Certificates') {
                            navigate('/dashboard/certificates');
                          } else if (module.id === 'Library') {
                            navigate('/dashboard/library');
                          } else if (module.id === 'Tickets') {
                            navigate('/dashboard/tickets');
                          } else if (module.id === 'PhotoGallery') {
                            navigate('/dashboard/photo-gallery');
                          }
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'translateY(-4px)';
                          e.currentTarget.style.boxShadow = '0 8px 16px rgba(0,0,0,0.1)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
                        }}
                      >
                        <div className="card-body">
                          <div className="d-flex align-items-start">
                            <div
                              className="rounded d-flex align-items-center justify-content-center me-3"
                              style={{
                                width: '48px',
                                height: '48px',
                                backgroundColor: `${module.color}20`,
                                color: module.color,
                              }}
                            >
                              <i className={`${module.icon} fs-4`}></i>
                            </div>
                            <div className="flex-grow-1">
                              <h6 className="mb-1 fw-bold">{module.name}</h6>
                              <p className="text-muted small mb-0">{module.desc}</p>
                            </div>
                            {(module.id === 'SIS' || module.id === 'HR' || module.id === 'Academics' || module.id === 'Finance' || module.id === 'Analytics' || module.id === 'Schedule' || module.id === 'Transport' || module.id === 'Certificates' || module.id === 'Library' || module.id === 'Tickets') && (
                              <i className="bi bi-arrow-right-circle text-primary-custom"></i>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="col-lg-4">
            {/* Quick Actions */}
            <div className="card border-0 shadow-sm mb-4">
              <div className="card-body">
                <h5 className="card-title mb-3">
                  <i className="bi bi-lightning-charge-fill me-2 text-warning"></i>
                  Quick Actions
                </h5>
                <div className="d-grid gap-2">
                  {quickActions.map((action, idx) => (
                    <button
                      key={idx}
                      className="btn btn-outline-secondary text-start d-flex align-items-center"
                      onClick={action.action}
                      style={{
                        borderColor: action.color,
                        color: action.color,
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = action.color;
                        e.currentTarget.style.color = '#fff';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                        e.currentTarget.style.color = action.color;
                      }}
                    >
                      <i className={`${action.icon} me-2`}></i>
                      {action.label}
                      <i className="bi bi-chevron-right ms-auto"></i>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Calendar Widget */}
            <div className="mb-4">
              <CalendarWidget domain={domain} />
            </div>

            {/* Recent Activity */}
            <div className="card border-0 shadow-sm">
              <div className="card-body">
                <h5 className="card-title mb-3">
                  <i className="bi bi-clock-history me-2 text-info"></i>
                  Recent Activity
                </h5>
                <div className="list-group list-group-flush">
                  {recentActivities.length === 0 ? (
                    <div className="text-center text-muted py-3">
                      <i className="bi bi-inbox fs-4 d-block mb-2"></i>
                      <small>No recent activity</small>
                    </div>
                  ) : (
                    recentActivities.map((activity) => (
                      <div key={activity.id} className="list-group-item px-0 py-2 border-0">
                        <div className="d-flex justify-content-between align-items-start">
                          <div className="d-flex align-items-start">
                            <i className={`bi ${
                              activity.type === 'announcement' ? 'bi-megaphone text-danger' :
                              activity.type === 'payment' ? 'bi-cash-coin text-success' :
                              activity.type === 'attendance' ? 'bi-calendar-check text-primary' :
                              activity.type === 'admission' ? 'bi-person-plus text-info' :
                              'bi-activity text-secondary'
                            } me-2 mt-1`}></i>
                            <div>
                              <small className="fw-semibold d-block">{activity.title}</small>
                              <small className="text-muted">{activity.description}</small>
                            </div>
                          </div>
                          <small className="text-muted text-nowrap ms-2">
                            {formatTimeAgo(activity.timestamp)}
                          </small>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <footer className="mt-5 py-4 text-center text-muted">
          <div className="mb-2">
            <small>
              My Learning Board &copy; 2025 | Academic Year: 2024-2025
            </small>
          </div>
          <div>
            <small>
              <a href="#" className="text-decoration-none me-3">Terms of Service</a>
              <a href="#" className="text-decoration-none me-3">Privacy Policy</a>
              <a href="#" className="text-decoration-none me-3" onClick={(e) => { e.preventDefault(); setActiveModule('Contact'); }}>Contact Us</a>
              <a href="#" className="text-decoration-none" onClick={(e) => { e.preventDefault(); setIsChatOpen(true); }}>Support</a>
            </small>
          </div>
        </footer>
      </div>

      {/* Floating AI Chat Assistant */}
      <FloatingAIChat
        domain={domain}
        externalOpen={isChatOpen}
        onOpenChange={setIsChatOpen}
      />
    </div>
  );
};

export default Dashboard;